import { Box, Button, Chip, Switch, Typography } from '@mui/material';
import { FC } from 'react';
import { CheckMark, FreeIcon, StandardICon } from '../../../shared/lib/assets';
import palette from '../../../shared/lib/theme/palette';
import {
  ChipStyle,
  flexCenter,
  planCardCurrencyWeb,
  planCardFreeIconSpacerCollapsedWeb,
  planCardFreeIconSpacerWeb,
  planCardIconFreeWeb,
  planCardIconWeb,
  planCardPriceMultiPlanWeb,
  planCardPriceRowWeb,
  planCardPriceSuffixWeb,
  planCardPriceWeb,
  planCardPromoWeb,
  planCardSelectButtonWeb,
  planCardTaxNoteWeb,
  planCardTitleWeb,
  planCardToggleWrapperWeb,
  planCardWrapperWeb
} from '../../../shared/lib/theme/styleConstants';
import { LABEL_CURRENT_PLAN, LABEL_NOT_AVAILABLE, LABEL_PLAN_POPULAR, LABEL_PLAN_BASIC, LABEL_PLAN_STANDARD, NOTE_TAXES_NOTICE, LABEL_PLAN_ANNUAL, LABEL_PLAN_ANNUAL_SAVE, LABEL_PLAN_MONTHLY } from '../../../shared/lib/constants';
import { FREE_AMOUNT, SelectedPlanType } from '../app/constants';
import { PlanDetailsCreateSubscriptionProps } from '../app/interfaceTypes';
import { PAuthActionType } from '../app/reducer/PAuthReducer';
import { getInterval } from '../app/utils';
import { Plan } from '../generated';
import CheckIcon from '@mui/icons-material/Check';
import { withTrademark } from '../../../shared/lib/common/Tokenizer';
import { useMobileView } from '../app/hooks/useMobileView';

export const PlanDetailsCreateSubscription: FC<PlanDetailsCreateSubscriptionProps> = ({ product, dispatch, setBillingCard, state }) => {
  const { selectedPlanValue, freePlanUsed, subscribedPlanId } = state ?? {};
  const isMobile = useMobileView();

  const label = { inputProps: { 'aria-label': 'Switch demo' } };
  const {
    blackTwo,
    blackOne,
    primary: { main: primaryMain }
  } = palette;

  const toggleSwitch = () => {
    dispatch({ type: PAuthActionType.SET_SELECTED_PLAN_VALUE, selectedPlanValue: selectedPlanValue === SelectedPlanType.MONTH ? SelectedPlanType.YEAR : SelectedPlanType.MONTH });
  };

  const planToShow = product.length > 1 ? product.filter((plan) => plan.interval === selectedPlanValue) : product;

  const goToBilling = (plan: Plan) => {
    dispatch?.({ type: PAuthActionType.SET_IS_FREE, isFree: plan.amount === FREE_AMOUNT ? true : false });
    dispatch?.({ type: PAuthActionType.SET_SELECTED_PLAN, selectedPlan: plan });
    dispatch({ type: PAuthActionType.SET_YEARLY_PAYMENT_VALUE, yearlyPaymentValue: selectedPlanValue === SelectedPlanType.YEAR });
    setBillingCard?.(true);
  };

  const renderMobileCard = (plan: Plan) => {
    const { id, amount, metadata, interval, trial_period_days, productDetails } = plan ?? {};
    const { PromotionMessage, productCount } = metadata ?? {};
    const { marketing_features } = productDetails || {};
    const amountUpdated = amount ? amount / 100 : 0;
    const selectButtonDisabled = (amount === FREE_AMOUNT && freePlanUsed) || id === subscribedPlanId;
    const isFree = amount === FREE_AMOUNT;

    return (
      <Box key={id} sx={{ opacity: isFree && freePlanUsed ? 0.7 : 1, width: '100%', minWidth: '100%', flexShrink: 0, px: 2, alignSelf: 'stretch', display: 'flex' }}>
        <Box sx={{
          borderRadius: '16px',
          backgroundColor: 'white',
          boxShadow: '0px 8px 24px rgba(145, 158, 171, 0.2)',
          padding: '20px 20px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          position: 'relative',
        }}>

          {/* POPULAR badge — top right */}
          {!isFree && (
            <Box sx={{ position: 'absolute', top: '15px', right: '10px', backgroundColor: primaryMain, borderRadius: '5px', px: 1, py: 0.25 }}>
              <Typography sx={{ color: 'white', fontSize: '5px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {LABEL_PLAN_POPULAR}
              </Typography>
            </Box>
          )}

          {/* Selected plan — top left blue tick */}
          {id === subscribedPlanId && (
            <Box sx={{ position: 'absolute', top: '10px', left: '10px' }}>
              <CheckIcon sx={{ color: primaryMain, fontSize: 18 }} />
            </Box>
          )}

          {/* Plan name */}
          <Typography sx={{ fontSize: '13px', fontWeight: '800', color: blackTwo, textTransform: 'uppercase', letterSpacing: '1.5px', mb: 1, mt: '20px' }}>
            {isFree ? LABEL_PLAN_BASIC : LABEL_PLAN_STANDARD}
          </Typography>

          {/* Price row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', mb: 0.5 }}>
            {!isFree && (
              <Typography sx={{ fontSize: '18px', fontWeight: '700', color: blackOne, mt: '8px', lineHeight: 1 }}>$</Typography>
            )}
            <Typography style={{ fontSize: '45px', fontWeight: '800', color: blackOne, lineHeight: 1 }}>
              {isFree ? 'Free' : amountUpdated.toFixed(2)}
            </Typography>
            {!isFree && (
              <Typography sx={{ fontSize: '11px', color: blackTwo, mt: 'auto', mb: '4px', ml: '3px', fontWeight: '500' }}>
                /{getInterval(interval)}
              </Typography>
            )}
            {isFree && (
              <Typography sx={{ fontSize: '11px', color: blackTwo, mt: 'auto', mb: '4px', ml: '3px' }}>
                /{trial_period_days} days
              </Typography>
            )}
          </Box>

          {/* Promotion message — always reserve space */}
          <Typography sx={{ fontSize: '11px', fontWeight: '600', mb: 1, textAlign: 'center', visibility: PromotionMessage ? 'visible' : 'hidden', color: primaryMain }}>
            {PromotionMessage || 'placeholder'}
          </Typography>

          {/* Plan icon */}
          <Box sx={{ my: '-20px', transform: 'scale(0.6)', transformOrigin: 'center', lineHeight: 0 }}>
            {isFree ? FreeIcon() : StandardICon()}
          </Box>

          {/* Monthly / Annual toggle — always reserve space, hidden for free plan */}
          <Box sx={{ width: '100%', mb: 0.5, mt: '-20px', visibility: productCount > 1 ? 'visible' : 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: '700', color: blackOne, textTransform: 'uppercase' }}>
                {LABEL_PLAN_MONTHLY}
              </Typography>
              <Switch
                {...label}
                onChange={toggleSwitch}
                checked={selectedPlanValue === SelectedPlanType.YEAR}
                sx={{
                  '& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb': { backgroundColor: primaryMain },
                  '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track': { backgroundColor: `${primaryMain}60`, opacity: 1 },
                }}
              />
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: '700', color: blackOne, textTransform: 'uppercase', lineHeight: 1.2 }}>
                  {LABEL_PLAN_ANNUAL}
                </Typography>
                <Typography sx={{ fontSize: '10px', fontStyle: 'italic', color: primaryMain, fontWeight: '600' }}>
                  {LABEL_PLAN_ANNUAL_SAVE}
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: '10px', color: blackTwo, textAlign: 'center', mt: 0.5 }}>
              {NOTE_TAXES_NOTICE}
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ width: '100%', height: '1px', backgroundColor: 'rgba(145,158,171,0.2)', mb: 1.5 }} />

          {/* Features */}
          <Box sx={{ width: '100%', mb: 2 }}>
            {marketing_features?.map((feature, index) => (
              <Typography key={index} sx={{ fontSize: '12px', color: blackTwo, lineHeight: 1.6, textAlign: 'left' }}>
                ✓ {withTrademark(feature.name)}
              </Typography>
            ))}
          </Box>

          {/* Select button — always at bottom */}
          <Button
            disabled={selectButtonDisabled}
            onClick={() => goToBilling(plan)}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ fontSize: '14px', py: 1.25, borderRadius: '10px', textTransform: 'none', fontWeight: '700', boxShadow: 'none', mt: 'auto' }}
          >
            {id === subscribedPlanId ? LABEL_CURRENT_PLAN : 'Select'}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderDesktopCard = (plan: Plan) => {
    const { id, amount, metadata, interval, trial_period_days, productDetails } = plan ?? {};
    const { PromotionMessage, productCount } = metadata ?? {};
    const { marketing_features } = productDetails || {};
    const amountUpdated = amount ? amount / 100 : 0;
    const selectButtonDisabled = (amount === FREE_AMOUNT && freePlanUsed) || id === subscribedPlanId;

    return (
      <Box key={id} sx={{ opacity: amount === FREE_AMOUNT && freePlanUsed ? 0.7 : 1 }} display="flex" minWidth={{ xs: '100%', md: '50%', xl: '32.7%' }} pb="40px">
        <Box sx={planCardWrapperWeb}>
          {id === subscribedPlanId && (
            <Box position="absolute" left="10px" top="10px">
              <CheckIcon sx={{ color: primaryMain }} />
            </Box>
          )}
          {amount !== FREE_AMOUNT && (
            <Box position="absolute" right="10px" top="10px">
              <Chip size="small" label={LABEL_PLAN_POPULAR} sx={[ChipStyle, { textTransform: 'uppercase', color: blackTwo }]} />
            </Box>
          )}

          <Typography variant="h2" sx={planCardTitleWeb}>
            {amount === FREE_AMOUNT ? LABEL_PLAN_BASIC : LABEL_PLAN_STANDARD}
          </Typography>

          <Box sx={planCardPriceRowWeb}>
            {amount !== FREE_AMOUNT && (
              <Typography variant="h2" sx={planCardCurrencyWeb}>
                $
              </Typography>
            )}

            <Typography variant="subtitle1" sx={amount === FREE_AMOUNT && productCount > 1 ? planCardPriceMultiPlanWeb : planCardPriceWeb}>
              {amount === FREE_AMOUNT ? amountUpdated === 0 && 'Free' : amountUpdated.toFixed(2)}
            </Typography>

            <Typography variant="subtitle2" sx={planCardPriceSuffixWeb}>
              {amount === FREE_AMOUNT ? `/ ${trial_period_days} Days` : `/ ${getInterval(interval)}`}
            </Typography>
          </Box>

          <Typography variant="h2" sx={planCardPromoWeb}>
            {PromotionMessage || <Box sx={{ opacity: 0 }}>{LABEL_NOT_AVAILABLE}</Box>}
          </Typography>

          <Box sx={productCount > 1 ? planCardIconWeb : planCardIconFreeWeb}>
            {productCount > 1 ? (
              StandardICon()
            ) : (
              <Box sx={selectedPlanValue ? planCardFreeIconSpacerWeb : planCardFreeIconSpacerCollapsedWeb}>{FreeIcon()}</Box>
            )}
          </Box>

          {productCount > 1 && (
            <Box sx={planCardToggleWrapperWeb}>
              <Box sx={flexCenter}>
                <Typography variant="h2" sx={{ textTransform: 'uppercase', color: blackOne, fontWeight: '700' }}>
                  {LABEL_PLAN_MONTHLY}
                </Typography>
                <Switch
                  {...label}
                  onChange={toggleSwitch}
                  checked={selectedPlanValue === SelectedPlanType.YEAR}
                  sx={{
                    '& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb': { backgroundColor: primaryMain },
                    '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track': { backgroundColor: `${primaryMain}60`, opacity: 1 },
                  }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="h2" sx={{ textTransform: 'uppercase', color: blackOne, fontWeight: '700' }}>
                    {LABEL_PLAN_ANNUAL}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 12, fontStyle: 'italic', color: blackTwo }}>
                    {LABEL_PLAN_ANNUAL_SAVE}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="h2" sx={planCardTaxNoteWeb}>
                {NOTE_TAXES_NOTICE}
              </Typography>
            </Box>
          )}

          {marketing_features?.map((feature, index) => (
            <Box key={index} display="flex" justifyContent="start">
              <Box component="span" mr={2}>
                {CheckMark()}
              </Box>
              <Typography variant="subtitle2">{withTrademark(feature.name)}</Typography>
            </Box>
          ))}

          <Button sx={planCardSelectButtonWeb} disabled={selectButtonDisabled} onClick={() => goToBilling(plan)} variant="contained" color="primary" fullWidth>
            {id === subscribedPlanId ? LABEL_CURRENT_PLAN : 'Select'}
          </Button>
        </Box>
      </Box>
    );
  };

  if (isMobile) {
    return planToShow?.map((plan) => renderMobileCard(plan));
  } else {
    return planToShow?.map((plan) => renderDesktopCard(plan));
  }
};
